import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { api, ZONE_DETAILS, TYPE_SLUGS, EMIRATE_SLUGS, PROPERTY_TYPES } from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import useSEO from "../lib/useSEO";
import { breadcrumbLd, itemListLd, zonePlaceLd, ZONE_GEO } from "../lib/jsonld";

// Resolve a zone slug back to its display name (case-insensitive)
function resolveZone(zoneSlug) {
  if (!zoneSlug) return null;
  for (const [name, details] of Object.entries(ZONE_DETAILS)) {
    if (details.slug === zoneSlug) return { name, ...details };
  }
  return null;
}

function buildSeo({ emirateName, zone, typeInfo }) {
  const parts = [];
  if (typeInfo) parts.push(typeInfo.label);
  if (zone) parts.push(`in ${zone.name}`);
  if (emirateName && !zone) parts.push(`in ${emirateName}`);
  if (zone && emirateName !== "Dubai") parts.push(`${emirateName}`);
  const h1 = parts.length > 0 ? parts.join(" ") : `Industrial Real Estate in ${emirateName}`;

  const descBits = [];
  if (typeInfo) descBits.push(`Browse ${typeInfo.plural}`);
  else descBits.push("Browse industrial real estate listings");
  if (zone) descBits.push(`in ${zone.name}, ${emirateName}`);
  else descBits.push(`across ${emirateName}`);
  descBits.push("— curated by WarehousesUAE. Updated daily from our live inventory.");

  return {
    title: `${h1} | WarehousesUAE`,
    description: descBits.join(" "),
    h1,
  };
}

export default function ZoneLanding() {
  const { emirate: emirateSlug, zone: zoneSlug, type: typeSlug } = useParams();

  // Try to interpret the second path segment: it might be a zone OR a type
  const emirateName = EMIRATE_SLUGS[emirateSlug];
  let zone = resolveZone(zoneSlug);
  let typeInfo = TYPE_SLUGS[typeSlug];

  // Handle /uae/:emirate/:typeOrZone — disambiguate
  if (zoneSlug && !zone && !typeInfo) {
    // Maybe zoneSlug is actually a type slug
    if (TYPE_SLUGS[zoneSlug]) typeInfo = TYPE_SLUGS[zoneSlug];
  }
  if (zoneSlug && TYPE_SLUGS[zoneSlug] && !typeSlug) {
    typeInfo = TYPE_SLUGS[zoneSlug];
    zone = null;
  }

  const [properties, setProperties] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const fetchProps = React.useCallback(() => {
    if (!emirateName) return;
    const params = new URLSearchParams();
    params.set("emirate", emirateName);
    if (zone) params.set("zone", zone.name);
    if (typeInfo?.property_type) params.set("property_type", typeInfo.property_type);
    if (typeInfo?.rent_sale) params.set("rent_sale", typeInfo.rent_sale);
    params.set("limit", "100");
    api.get(`/properties?${params.toString()}`).then((r) => {
      setProperties(r.data);
      setLoading(false);
    });
  }, [emirateName, zone, typeInfo]);

  React.useEffect(fetchProps, [fetchProps]);

  const seo = buildSeo({ emirateName, zone, typeInfo });
  const canonical = `/uae/${emirateSlug}${zoneSlug ? `/${zoneSlug}` : ""}${typeSlug ? `/${typeSlug}` : ""}`;

  const jsonLd = React.useMemo(() => {
    if (!emirateName) return [];
    const trail = [
      { name: "Home", url: "/" },
      { name: "UAE", url: "/uae" },
      { name: emirateName, url: `/uae/${emirateSlug}` },
    ];
    if (zone) trail.push({ name: zone.name, url: `/uae/${emirateSlug}/${zoneSlug}` });
    if (typeInfo) trail.push({ name: typeInfo.label, url: canonical });
    const blocks = [breadcrumbLd(trail)];
    if (zone && ZONE_GEO[zone.name]) blocks.push(zonePlaceLd(zone.name, zone, properties.length));
    if (properties.length > 0) blocks.push(itemListLd(properties));
    return blocks;
  }, [emirateName, emirateSlug, zone, zoneSlug, typeInfo, canonical, properties]);

  useSEO({ title: seo.title, description: seo.description, canonical, jsonLd });

  if (!emirateSlug) {
    // /uae — render a UAE-wide overview with emirate cards
    return (
      <div className="min-h-screen bg-zinc-50">
        <section className="bg-[#031833] text-white">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16">
            <div className="font-mono-tech text-[10px] uppercase text-[#FFCC00] mb-3">// UAE</div>
            <h1 className="font-display text-5xl md:text-6xl tracking-tighter mb-4 max-w-4xl uppercase">
              Industrial Real Estate across the United Arab Emirates
            </h1>
            <p className="font-mono-tech text-sm text-zinc-300 max-w-2xl leading-relaxed">
              Live inventory of warehouses, industrial units, plots, storage, showrooms, logistics hubs and labour accommodation across all seven UAE emirates. Curated by WarehousesUAE.
            </p>
          </div>
        </section>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Object.entries(EMIRATE_SLUGS).map(([slug, name]) => (
              <Link
                key={slug}
                to={`/uae/${slug}`}
                data-testid={`uae-emirate-${slug}`}
                className="group block bg-white border border-zinc-200 hover:border-[#031833] p-6 transition-colors"
              >
                <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-2">EMIRATE</div>
                <h2 className="font-display text-2xl tracking-tighter mb-1">{name}</h2>
                <div className="font-mono-tech text-[11px] uppercase text-zinc-500 group-hover:text-[#FFCC00] transition-colors">View inventory →</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!emirateName) return <Navigate to="/properties" replace />;

  const intro = (() => {
    const lines = [];
    if (zone && zone.description) lines.push(zone.description);
    if (typeInfo && zone) {
      lines.push(`Browse our live inventory of ${typeInfo.plural} in ${zone.name}. Each listing is verified by the WarehousesUAE acquisition team and includes power capacity, ceiling height, loading bays and current asking price.`);
    } else if (typeInfo) {
      lines.push(`Looking for ${typeInfo.plural} in ${emirateName}? We aggregate the active commercial inventory from owners, landlords and trusted partners across the emirate. Send us a brief and we'll shortlist the right options within 24 hours.`);
    } else if (zone) {
      lines.push(`Explore the full inventory available in ${zone.name}. Warehouses, industrial units, plots, showrooms and accommodation — filtered to one place.`);
    } else {
      lines.push(`${emirateName} is one of the UAE's key industrial markets. From free-zone bonded logistics to mid-size workshops and labour accommodation, WarehousesUAE consolidates the live commercial inventory for occupiers and investors.`);
    }
    return lines;
  })();

  // Type cross-link suggestions when no type is selected
  const otherTypes = !typeInfo ? Object.entries(TYPE_SLUGS).slice(0, 6) : [];

  // Zone cross-link suggestions when in Dubai and no zone selected
  const dubaiZones = emirateSlug === "dubai" && !zone ? Object.values(ZONE_DETAILS).slice(0, 8) : [];

  return (
    <div className="min-h-screen bg-zinc-50">
      <section className="bg-[#031833] text-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16">
          <nav data-testid="seo-breadcrumb" className="font-mono-tech text-[10px] uppercase text-zinc-400 mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2 text-zinc-600">/</span>
            <Link to="/uae" className="hover:text-white">UAE</Link>
            <span className="mx-2 text-zinc-600">/</span>
            <Link to={`/uae/${emirateSlug}`} className="hover:text-white">{emirateName}</Link>
            {zone && <>
              <span className="mx-2 text-zinc-600">/</span>
              <Link to={`/uae/${emirateSlug}/${zoneSlug}`} className="hover:text-white">{zone.name}</Link>
            </>}
            {typeInfo && <>
              <span className="mx-2 text-zinc-600">/</span>
              <span className="text-[#FFCC00]">{typeInfo.label}</span>
            </>}
          </nav>
          <div className="font-mono-tech text-[10px] uppercase text-[#FFCC00] mb-3">// {emirateName.toUpperCase()}</div>
          <h1 data-testid="seo-h1" className="font-display text-4xl md:text-6xl tracking-tighter mb-5 max-w-4xl uppercase">
            {seo.h1}
          </h1>
          <p className="font-mono-tech text-sm text-zinc-300 max-w-2xl leading-relaxed">
            {seo.description}
          </p>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
        {/* Intro paragraphs (SEO content) */}
        <section className="prose-content max-w-3xl mb-10">
          {intro.map((p, i) => (
            <p key={i} className="text-zinc-700 leading-relaxed mb-3">{p}</p>
          ))}
        </section>

        {/* Cross-links: other types when none selected */}
        {otherTypes.length > 0 && (
          <section className="mb-10">
            <div className="font-mono-tech text-[10px] uppercase text-zinc-500 mb-3">// Browse by category in {emirateName}</div>
            <div className="flex flex-wrap gap-2">
              {otherTypes.map(([slug, t]) => (
                <Link
                  key={slug}
                  to={`/uae/${emirateSlug}/${zoneSlug ? `${zoneSlug}/` : ""}${slug}`}
                  data-testid={`xlink-type-${slug}`}
                  className="font-mono-tech text-[11px] uppercase border border-zinc-300 px-3 py-2 hover:bg-[#031833] hover:text-white hover:border-[#031833] transition-colors"
                >
                  {t.label} →
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Cross-links: Dubai zones */}
        {dubaiZones.length > 0 && (
          <section className="mb-10">
            <div className="font-mono-tech text-[10px] uppercase text-zinc-500 mb-3">// Popular Dubai zones</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {dubaiZones.map((z) => (
                <Link
                  key={z.slug}
                  to={`/uae/dubai/${z.slug}`}
                  data-testid={`xlink-zone-${z.slug}`}
                  className="group relative aspect-[3/2] overflow-hidden bg-zinc-900"
                >
                  <img src={z.img} alt="" className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 to-transparent" />
                  <div className="absolute inset-0 p-3 flex flex-col justify-end">
                    <div className="font-display text-lg tracking-tighter text-white">{z.slug.toUpperCase().replace(/-/g, " ")}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Listings */}
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-display text-3xl tracking-tighter">
            {loading ? "Loading inventory…" : `${properties.length} active listing${properties.length === 1 ? "" : "s"}`}
          </h2>
          <Link
            to={`/properties?${typeInfo?.property_type ? `property_type=${typeInfo.property_type}&` : ""}${typeInfo?.rent_sale ? `rent_sale=${typeInfo.rent_sale}&` : ""}${zone ? `zone=${encodeURIComponent(zone.name)}&` : ""}emirate=${encodeURIComponent(emirateName)}`}
            data-testid="seo-view-all"
            className="font-mono-tech text-[11px] uppercase text-[#031833] hover:text-[#FFCC00] hover:bg-[#031833] hover:px-3 hover:py-1.5 transition-all"
          >
            View all in /properties →
          </Link>
        </div>

        {loading ? null : properties.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-12 text-center bg-white">
            <div className="font-display text-2xl tracking-tighter mb-2">No active inventory right now</div>
            <p className="text-zinc-500 text-sm mb-5">Tell us your requirement and we&apos;ll source it within 48 hours.</p>
            <Link to="/contact" className="inline-block font-mono-tech text-xs uppercase bg-[#031833] text-white px-5 py-3">Send a requirement →</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {properties.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
